import React, { useState, useEffect, useRef, useCallback } from 'react';
import {
  MdClose,
  MdCode,
  MdChat,
  MdEmail,
  MdChevronRight,
  MdChevronLeft,
  MdArrowBack,
  MdCheck,
} from 'react-icons/md';
import {
  FaWhatsapp,
  FaFacebookF,
  FaXTwitter,
  FaLinkedinIn,
  FaRedditAlien,
} from 'react-icons/fa6';
import { API_BASE_URL } from './config';
import './ShareModal.css';

// Helper to format seconds to M:SS or H:MM:SS
function formatTimestamp(totalSeconds) {
  const sec = Math.max(0, Math.floor(totalSeconds || 0));
  const hours = Math.floor(sec / 3600);
  const minutes = Math.floor((sec % 3600) / 60);
  const remainingSeconds = sec % 60;

  if (hours > 0) {
    return `${hours}:${minutes.toString().padStart(2, '0')}:${remainingSeconds.toString().padStart(2, '0')}`;
  }
  return `${minutes}:${remainingSeconds.toString().padStart(2, '0')}`;
}

// Helper to parse timestamp string (e.g. "1:24" or "84") back to total seconds
function parseTimestamp(str) {
  if (!str) return 0;
  const parts = str.trim().split(':').map((p) => parseInt(p, 10) || 0);
  if (parts.length === 3) {
    return parts[0] * 3600 + parts[1] * 60 + parts[2];
  }
  if (parts.length === 2) {
    return parts[0] * 60 + parts[1];
  }
  if (parts.length === 1) {
    return parts[0];
  }
  return 0;
}

export default function ShareModal({
  isOpen,
  onClose,
  videoData,
  videoUrlId,
  currentTime = 0,
  showToast,
}) {
  const [shouldRender, setShouldRender] = useState(isOpen);
  const [isClosing, setIsClosing] = useState(false);
  const [isCopied, setIsCopied] = useState(false);
  const [isEmbedCopied, setIsEmbedCopied] = useState(false);
  const [startAtChecked, setStartAtChecked] = useState(false);
  const [timeText, setTimeText] = useState('0:00');
  const [isEmbedView, setIsEmbedView] = useState(false);
  const [canScrollLeft, setCanScrollLeft] = useState(false);
  const [canScrollRight, setCanScrollRight] = useState(true);

  const carouselRef = useRef(null);
  const closingTimeoutRef = useRef(null);
  const prevIsOpenRef = useRef(false);
  const onCloseRef = useRef(onClose);
  const shouldRenderRef = useRef(shouldRender);

  useEffect(() => {
    onCloseRef.current = onClose;
  }, [onClose]);

  useEffect(() => {
    shouldRenderRef.current = shouldRender;
  }, [shouldRender]);

  // Smooth open / close animation lifecycle
  useEffect(() => {
    let timer = null;
    if (isOpen) {
      setShouldRender(true);
      setIsClosing(false);
    } else if (shouldRenderRef.current) {
      setIsClosing(true);
      timer = setTimeout(() => {
        setShouldRender(false);
        setIsClosing(false);
      }, 200);
    }
    return () => {
      if (timer) clearTimeout(timer);
    };
  }, [isOpen]);

  // Capture timestamp ONCE strictly when modal is opened
  // DO NOT depend on currentTime so playing video in background never shifts the timeText or auto-ticks the checkbox!
  useEffect(() => {
    if (isOpen && !prevIsOpenRef.current) {
      const capturedSec = currentTime;
      const formatted = formatTimestamp(capturedSec);
      setTimeText(formatted);
      setIsCopied(false);
      setIsEmbedCopied(false);
      setIsEmbedView(false);
      // Auto-check "Start at" if video is already past 1 second when Share was clicked
      setStartAtChecked(capturedSec >= 1);
    }
    prevIsOpenRef.current = isOpen;
  }, [isOpen]);

  // Smooth close action trigger
  const handleSmoothClose = useCallback((e) => {
    if (e && e.stopPropagation) e.stopPropagation();
    setIsClosing(true);
    setTimeout(() => {
      setShouldRender(false);
      setIsClosing(false);
      if (onCloseRef.current) onCloseRef.current();
    }, 200);
  }, []);

  // Close on Escape key
  useEffect(() => {
    const handleKeyDown = (e) => {
      if (e.key === 'Escape' && shouldRender) {
        handleSmoothClose();
      }
    };
    window.addEventListener('keydown', handleKeyDown);
    return () => window.removeEventListener('keydown', handleKeyDown);
  }, [shouldRender, handleSmoothClose]);

  // Update carousel scroll buttons state
  const checkScroll = useCallback(() => {
    if (carouselRef.current) {
      const { scrollLeft, scrollWidth, clientWidth } = carouselRef.current;
      setCanScrollLeft(scrollLeft > 6);
      setCanScrollRight(scrollLeft < scrollWidth - clientWidth - 6);
    }
  }, []);

  useEffect(() => {
    const el = carouselRef.current;
    if (el) {
      checkScroll();
      el.addEventListener('scroll', checkScroll, { passive: true });
      window.addEventListener('resize', checkScroll);
      return () => {
        el.removeEventListener('scroll', checkScroll);
        window.removeEventListener('resize', checkScroll);
      };
    }
  }, [shouldRender, isEmbedView, checkScroll]);

  // Construct SpeedoTube URL
  const targetId = videoData?.videoUrlId || videoUrlId || 'HyRyzOCHONj';
  const origin = window.location.origin || 'http://localhost:3000';
  const baseWatchUrl = `${origin}/watch?v=${targetId}`;

  const currentSeconds = parseTimestamp(timeText);
  const finalShareUrl = startAtChecked && currentSeconds > 0
    ? `${baseWatchUrl}&t=${currentSeconds}s`
    : baseWatchUrl;

  const embedCode = `<iframe width="560" height="315" src="${origin}/watch?v=${targetId}${startAtChecked && currentSeconds > 0 ? `&start=${currentSeconds}` : ''}&embed=1" title="${videoData?.title || 'SpeedoTube video player'}" frameborder="0" allow="accelerometer; autoplay; clipboard-write; encrypted-media; gyroscope; picture-in-picture; web-share" referrerpolicy="strict-origin-when-cross-origin" allowfullscreen></iframe>`;

  // Backend telemetry logging
  const logShareToBackend = async (platform) => {
    try {
      const videoId = videoData?.id || 157;
      await fetch(`${API_BASE_URL}/api/v1/videos/${videoId}/share`, {
        method: 'POST',
        headers: {
          'Content-Type': 'application/json',
          devicetype: '0',
          token: 'c7e993808a344da9d59cfa20fa8b6061',
          request_type: 'USER',
        },
        body: JSON.stringify({
          platform,
          timestamp: startAtChecked ? timeText : null,
          url: finalShareUrl,
        }),
      });
    } catch (e) {
      console.warn('Backend share telemetry error:', e);
    }
  };

  // Copy share URL to clipboard
  const handleCopyLink = () => {
    if (navigator.clipboard) {
      navigator.clipboard.writeText(finalShareUrl);
      setIsCopied(true);
      if (showToast) showToast('Link copied to clipboard');
      logShareToBackend('copy');
      setTimeout(() => setIsCopied(false), 2500);
    }
  };

  // Copy embed snippet
  const handleCopyEmbed = () => {
    if (navigator.clipboard) {
      navigator.clipboard.writeText(embedCode);
      setIsEmbedCopied(true);
      if (showToast) showToast('Embed code copied to clipboard');
      logShareToBackend('embed');
      setTimeout(() => setIsEmbedCopied(false), 2500);
    }
  };

  // Carousel scroll helpers
  const scrollCarousel = (direction) => {
    if (carouselRef.current) {
      const scrollAmount = direction === 'left' ? -220 : 220;
      carouselRef.current.scrollBy({ left: scrollAmount, behavior: 'smooth' });
    }
  };

  // Social share triggers
  const handleSocialShare = (platform, url) => {
    logShareToBackend(platform);
    if (url) {
      window.open(url, '_blank', 'noopener,noreferrer,width=650,height=550');
    }
  };

  if (!shouldRender) return null;

  return (
    <div
      className={`yt-share-modal-overlay ${isClosing ? 'closing' : ''}`}
      onClick={handleSmoothClose}
    >
      <div
        className={`yt-share-modal-container ${isClosing ? 'closing' : ''}`}
        onClick={(e) => e.stopPropagation()}
        role="dialog"
        aria-modal="true"
      >
        {/* Header */}
        <div className="yt-share-modal-header">
          {isEmbedView ? (
            <div className="yt-share-embed-header-left">
              <button
                className="yt-share-back-btn"
                onClick={() => setIsEmbedView(false)}
                aria-label="Back to share options"
              >
                <MdArrowBack size={20} />
              </button>
              <h3 className="yt-share-modal-title">Embed Video</h3>
            </div>
          ) : (
            <h3 className="yt-share-modal-title">Share</h3>
          )}
          <button
            className="yt-share-close-btn"
            onClick={handleSmoothClose}
            aria-label="Close"
          >
            <MdClose size={22} />
          </button>
        </div>

        {/* Modal Body: Embed View or Standard Carousel View */}
        {isEmbedView ? (
          <div className="yt-share-embed-content">
            <div className="yt-share-embed-preview-wrapper">
              <div className="yt-share-embed-mock-frame">
                <span className="yt-share-embed-preview-title">
                  {videoData?.title || 'SpeedoTube Player'}
                </span>
                <span className="yt-share-embed-preview-sub">
                  Embedded preview will render on your webpage
                </span>
              </div>
            </div>

            <div className="yt-share-embed-code-box">
              <textarea
                className="yt-share-embed-textarea"
                readOnly
                value={embedCode}
                rows={4}
              />
            </div>

            <div className="yt-share-embed-footer">
              <label className="yt-share-start-checkbox-row">
                <input
                  type="checkbox"
                  checked={startAtChecked}
                  onChange={(e) => setStartAtChecked(e.target.checked)}
                  className="yt-share-custom-checkbox"
                />
                <span className="yt-share-start-label">Start at</span>
                <input
                  type="text"
                  value={timeText}
                  onChange={(e) => setTimeText(e.target.value)}
                  className="yt-share-time-input"
                  aria-label="Start time"
                />
              </label>

              <button
                className={`yt-share-copy-btn ${isEmbedCopied ? 'copied' : ''}`}
                onClick={handleCopyEmbed}
              >
                {isEmbedCopied ? (
                  <>
                    <MdCheck size={16} /> Copied
                  </>
                ) : (
                  'Copy'
                )}
              </button>
            </div>
          </div>
        ) : (
          <div className="yt-share-modal-main">
            {/* Social / Sharing targets carousel with smooth gradient edge mask */}
            <div className="yt-share-carousel-wrapper">
              {canScrollLeft && (
                <div className="yt-share-nav-gradient left">
                  <button
                    className="yt-share-nav-btn left"
                    onClick={() => scrollCarousel('left')}
                    aria-label="Scroll left"
                  >
                    <MdChevronLeft size={22} />
                  </button>
                </div>
              )}

              <div className="yt-share-carousel" ref={carouselRef}>
                {/* 1. Embed */}
                <button
                  className="yt-share-target-item"
                  onClick={() => setIsEmbedView(true)}
                >
                  <div className="yt-share-target-circle embed-circle">
                    <MdCode size={26} />
                  </div>
                  <span className="yt-share-target-name">Embed</span>
                </button>

                {/* 2. WhatsApp */}
                <button
                  className="yt-share-target-item"
                  onClick={() =>
                    handleSocialShare(
                      'whatsapp',
                      `https://api.whatsapp.com/send?text=${encodeURIComponent(
                        (videoData?.title ? videoData.title + '\n' : '') + finalShareUrl
                      )}`
                    )
                  }
                >
                  <div className="yt-share-target-circle whatsapp-circle">
                    <FaWhatsapp size={26} />
                  </div>
                  <span className="yt-share-target-name">WhatsApp</span>
                </button>

                {/* 3. Messages */}
                <button
                  className="yt-share-target-item"
                  onClick={() =>
                    handleSocialShare(
                      'messages',
                      `sms:?body=${encodeURIComponent(finalShareUrl)}`
                    )
                  }
                >
                  <div className="yt-share-target-circle messages-circle">
                    <MdChat size={24} />
                  </div>
                  <span className="yt-share-target-name">Messages</span>
                </button>

                {/* 4. Facebook */}
                <button
                  className="yt-share-target-item"
                  onClick={() =>
                    handleSocialShare(
                      'facebook',
                      `https://www.facebook.com/sharer/sharer.php?u=${encodeURIComponent(
                        finalShareUrl
                      )}`
                    )
                  }
                >
                  <div className="yt-share-target-circle facebook-circle">
                    <FaFacebookF size={20} />
                  </div>
                  <span className="yt-share-target-name">Facebook</span>
                </button>

                {/* 5. X */}
                <button
                  className="yt-share-target-item"
                  onClick={() =>
                    handleSocialShare(
                      'x',
                      `https://twitter.com/intent/tweet?url=${encodeURIComponent(
                        finalShareUrl
                      )}&text=${encodeURIComponent(videoData?.title || 'SpeedoTube Video')}`
                    )
                  }
                >
                  <div className="yt-share-target-circle x-circle">
                    <FaXTwitter size={20} />
                  </div>
                  <span className="yt-share-target-name">X</span>
                </button>

                {/* 6. Email */}
                <button
                  className="yt-share-target-item"
                  onClick={() =>
                    handleSocialShare(
                      'email',
                      `mailto:?subject=${encodeURIComponent(
                        videoData?.title || 'Watch this video on SpeedoTube'
                      )}&body=${encodeURIComponent(finalShareUrl)}`
                    )
                  }
                >
                  <div className="yt-share-target-circle email-circle">
                    <MdEmail size={22} />
                  </div>
                  <span className="yt-share-target-name">Email</span>
                </button>

                {/* 7. LinkedIn */}
                <button
                  className="yt-share-target-item"
                  onClick={() =>
                    handleSocialShare(
                      'linkedin',
                      `https://www.linkedin.com/sharing/share-offsite/?url=${encodeURIComponent(
                        finalShareUrl
                      )}`
                    )
                  }
                >
                  <div className="yt-share-target-circle linkedin-circle">
                    <FaLinkedinIn size={20} />
                  </div>
                  <span className="yt-share-target-name">LinkedIn</span>
                </button>

                {/* 8. Reddit */}
                <button
                  className="yt-share-target-item"
                  onClick={() =>
                    handleSocialShare(
                      'reddit',
                      `https://www.reddit.com/submit?url=${encodeURIComponent(
                        finalShareUrl
                      )}&title=${encodeURIComponent(videoData?.title || 'SpeedoTube')}`
                    )
                  }
                >
                  <div className="yt-share-target-circle reddit-circle">
                    <FaRedditAlien size={22} />
                  </div>
                  <span className="yt-share-target-name">Reddit</span>
                </button>
              </div>

              {canScrollRight && (
                <div className="yt-share-nav-gradient right">
                  <button
                    className="yt-share-nav-btn right"
                    onClick={() => scrollCarousel('right')}
                    aria-label="Scroll right"
                  >
                    <MdChevronRight size={22} />
                  </button>
                </div>
              )}
            </div>

            {/* SpeedoTube Link Input Box with Copy Button */}
            <div className="yt-share-link-box">
              <input
                type="text"
                readOnly
                value={finalShareUrl}
                className="yt-share-link-input"
                aria-label="SpeedoTube video link"
                onClick={(e) => e.target.select()}
              />
              <button
                className={`yt-share-copy-btn ${isCopied ? 'copied' : ''}`}
                onClick={handleCopyLink}
              >
                {isCopied ? (
                  <>
                    <MdCheck size={16} /> Copied
                  </>
                ) : (
                  'Copy'
                )}
              </button>
            </div>

            {/* Divider line */}
            <div className="yt-share-divider" />

            {/* "Start at" Checkbox Row */}
            <div className="yt-share-footer-row">
              <label className="yt-share-start-checkbox-row">
                <input
                  type="checkbox"
                  checked={startAtChecked}
                  onChange={(e) => setStartAtChecked(e.target.checked)}
                  className="yt-share-custom-checkbox"
                />
                <span className="yt-share-start-label">Start at</span>
                <input
                  type="text"
                  value={timeText}
                  onChange={(e) => setTimeText(e.target.value)}
                  className="yt-share-time-input"
                  aria-label="Start time"
                />
              </label>
            </div>
          </div>
        )}
      </div>
    </div>
  );
}
